import { reportTelemetryError, reportTelemetryLog } from './telemetry';

// Preserve known route names for grouping; every other segment may contain
// a user identifier, including percent-encoded names and email addresses.
const KNOWN_PATH_SEGMENTS = new Set<string>([
  'api',
  'auth',
  'oauth',
  'public',
  'knowledge',
  'search',
  'read_knowledge',
  'search_memory',
  'manage_classifiers',
  'manage_automations',
  'list_connectors',
  'list_connections',
  'list_entities',
  'list_entity_types',
  'list_agents',
  'list_members',
  'list_clients',
  'list_notifications',
  'list_oauth_apps',
  'classifiers',
  'connectors',
  'connector_definitions',
  'notifications',
  'clients',
  'agents',
  'events',
  'entities',
  'content-distribution',
  'auth-config',
  'auth-runs',
  'active',
  'organizations',
  'accounts',
  'features',
  'platforms',
  'callback',
  'login',
  'logout',
  'reset-password',
  'accept-invitation',
  'consent',
  'device',
  'automations',
  'feeds',
  'connections',
  'members',
  'oauth_apps',
  'get_automation',
]);

function sanitizePath(pathname: string): string {
  if (!pathname || pathname === '/') return pathname;
  const parts = pathname.split('/');
  const sanitized = parts.map((part) => {
    if (part === '') return part;
    if (KNOWN_PATH_SEGMENTS.has(part)) return part;
    return ':s';
  });
  return sanitized.join('/');
}

function extractRequestInfo(input: RequestInfo | URL, init?: RequestInit) {
  let url: string;
  let method: string;
  if (typeof input === 'string') {
    url = input;
    method = (init?.method ?? 'GET').toUpperCase();
  } else if (input instanceof URL) {
    url = input.toString();
    method = (init?.method ?? 'GET').toUpperCase();
  } else {
    url = input.url;
    method = (init?.method ?? input.method ?? 'GET').toUpperCase();
  }

  // Drop the origin (tenant subdomains identify the workspace) and query,
  // then collapse dynamic segments so telemetry retains only the route family.
  let urlPattern: string;
  try {
    const parsed = new URL(url, typeof window !== 'undefined' ? window.location.origin : undefined);
    urlPattern = sanitizePath(parsed.pathname);
  } catch {
    urlPattern = '<unparseable>';
  }

  return { urlPattern, method };
}

// Statuses that fire frequently as part of normal app automation (validation,
// not-found-by-id lookups, conflicts, optimistic-locking) — reporting them
// creates alert fatigue. Anything outside this set stays at warning/error.
const EXPECTED_4XX = new Set<number>([400, 401, 403, 404, 409, 422]);

/**
 * Report a non-OK fetch response to telemetry. Skips successful responses,
 * auth-flow noise, and routine 4xx (see EXPECTED_4XX). 5xx → error;
 * unexpected 4xx (e.g. 412, 429) → warning.
 */
export function captureApiError(
  input: RequestInfo | URL,
  response: Response,
  init?: RequestInit,
): void {
  if (response.ok) return;
  if (EXPECTED_4XX.has(response.status)) return;

  const { urlPattern, method } = extractRequestInfo(input, init);
  const level: 'error' | 'warning' = response.status >= 500 ? 'error' : 'warning';
  reportTelemetryLog(`API ${response.status} ${method} ${urlPattern}`, level);
}

/**
 * Report a thrown fetch error (network failure, non-timeout abort).
 * The DEFAULT_TIMEOUT_MS abort in fetchWithTimeout is expected — callers
 * should filter AbortError before invoking this.
 */
export function captureApiException(
  input: RequestInfo | URL,
  error: unknown,
  init?: RequestInit,
): void {
  const { urlPattern, method } = extractRequestInfo(input, init);
  reportTelemetryError(error, { api_method: method, api_url_pattern: urlPattern });
}

/**
 * Report an error that surfaced through a route error boundary. React render
 * errors are swallowed by TanStack Router's error component and never reach
 * `window.onerror`, so without this they're invisible in telemetry.
 */
export function reportRouteError(error: unknown): void {
  reportTelemetryError(error, { source: 'route-error-boundary' });
}
